import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { diffAsSingleFile, type SectionDiff } from '@/lib/writer/diff';

interface State {
  loading: boolean;
  error: string | null;
  sections: SectionDiff[];
}

/**
 * Load `main_tex` (and `bib_source`) for two writer_versions and compute a
 * diff between them. Falls back to a whole-file diff — per-section snapshots
 * aren't stored per version (writer_project_sections is live/mutable), so the
 * assembled main_tex is the canonical historical text.
 *
 * `baseId` may be null to signal "no base" (e.g. the very first version); in
 * that case every non-empty line in head shows as an insertion.
 */
export function useWriterVersionDiff(baseId: string | null, headId: string | null) {
  const [state, setState] = useState<State>({ loading: false, error: null, sections: [] });

  useEffect(() => {
    if (!headId) {
      setState({ loading: false, error: null, sections: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const ids = baseId ? [baseId, headId] : [headId];
        const { data, error } = await supabase
          .from('writer_versions')
          .select('id, main_tex, bib_source')
          .in('id', ids);
        if (error) throw error;
        if (cancelled) return;
        const byId = new Map((data ?? []).map((r) => [r.id, r]));
        const head = byId.get(headId);
        const base = baseId ? byId.get(baseId) : null;
        const sections: SectionDiff[] = [];
        sections.push(
          diffAsSingleFile(base?.main_tex ?? '', head?.main_tex ?? '', 'main.tex'),
        );
        const baseBib = (base as { bib_source?: string } | null)?.bib_source ?? '';
        const headBib = (head as { bib_source?: string } | undefined)?.bib_source ?? '';
        if (baseBib || headBib) {
          sections.push(diffAsSingleFile(baseBib, headBib, 'references.bib'));
        }
        setState({ loading: false, error: null, sections });
      } catch (e) {
        if (cancelled) return;
        setState({
          loading: false,
          error: e instanceof Error ? e.message : 'Failed to load diff',
          sections: [],
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseId, headId]);

  return state;
}
